import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, Download, Trash2, Users, Send, MessageSquare, RefreshCcw, ChevronDown, ChevronRight, Bell, UserPlus, Ban } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import ManualRegistrationModal from "./ManualRegistrationModal";
import { TOURNAMENT } from "../../lib/constants";

// Payment status visual styling. Uses SOLID backgrounds (not /20 transparency)
// so the badge is always crisp on dark mobile screens.
const PAYMENT_STATUS_STYLE = {
    paid:               { label: "Confirmée",       bg: "bg-[#7FD93C]", text: "text-black" },
    pending_etransfer:  { label: "Attente Interac", bg: "bg-[#4FE0D5]", text: "text-black" },
    pending:            { label: "En attente",      bg: "bg-[#4FE0D5]", text: "text-black" },
    cancelled:          { label: "Annulée",         bg: "bg-rose-500",  text: "text-white" },
    expired:            { label: "Expirée",         bg: "bg-white/30",  text: "text-white" },
    failed:             { label: "Échec",           bg: "bg-rose-500",  text: "text-white" },
};

function PaymentBadge({ reg }) {
    const s = PAYMENT_STATUS_STYLE[reg.payment_status] || { label: reg.payment_status || "—", bg: "bg-white/20", text: "text-white" };
    return (
        <span className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-wide ${s.bg} ${s.text}`} data-testid={`payment-badge-${reg.id}`}>
            {s.label}{reg.manually_marked ? " ⚙" : ""}
        </span>
    );
}

// Twilio delivery statuses → display style.
const STATUS_STYLE = {
    queued: { label: "En file", color: "bg-white/10 text-white/70", icon: "⏳" },
    accepted: { label: "Accepté", color: "bg-white/10 text-white/70", icon: "⏳" },
    scheduled: { label: "Programmé", color: "bg-white/10 text-white/70", icon: "⏳" },
    sending: { label: "Envoi", color: "bg-yellow-500/20 text-yellow-300", icon: "📤" },
    sent: { label: "Envoyé", color: "bg-[#4FE0D5]/20 text-[#4FE0D5]", icon: "📤" },
    delivered: { label: "LIVRÉ", color: "bg-[#7FD93C]/30 text-[#7FD93C]", icon: "✅" },
    undelivered: { label: "NON LIVRÉ", color: "bg-rose-500/25 text-rose-300", icon: "❌" },
    failed: { label: "ÉCHEC", color: "bg-rose-500/30 text-rose-400", icon: "❌" },
};

const KIND_LABEL = {
    admin_new_registration: "SMS admins (nouvelle inscription)",
    team_magic_link: "SMS responsable (lien d'accès)",
    team_magic_link_resend: "SMS responsable (renvoi du lien)",
    admin_test: "SMS de test",
};

function StatusBadge({ row }) {
    const s = row.status || "queued";
    const style = STATUS_STYLE[s] || { label: s, color: "bg-white/10 text-white/70", icon: "·" };
    return (
        <span className={`px-2 py-0.5 angle-tag text-[10px] font-bold uppercase tracking-wider ${style.color}`} title={row.error_message || ""}>
            {style.icon} {style.label}
        </span>
    );
}

function ManagerSmsCell({ sms, label }) {
    if (!sms) return <span className="text-white/30 text-xs">—</span>;
    const s = sms.status;
    if (!s) return <span className="text-white/40 text-xs">en attente</span>;
    const style = STATUS_STYLE[s] || { label: s, color: "bg-white/10 text-white/70", icon: "·" };
    const tooltip = [
        label,
        sms.to,
        sms.error_code ? `Code ${sms.error_code}` : null,
        sms.error_message,
    ].filter(Boolean).join(" — ");
    return (
        <div className="flex flex-col items-start gap-0.5">
            <span className={`px-2 py-0.5 angle-tag text-[10px] font-bold uppercase tracking-wider ${style.color}`} title={tooltip}>
                {style.icon} {style.label}
            </span>
            {(s === "undelivered" || s === "failed") && sms.error_code ? (
                <span className="text-[9px] text-rose-300/80 font-mono">code {sms.error_code}</span>
            ) : null}
        </div>
    );
}

function SmsRow({ row }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1.5 px-2 border-l-2 border-white/10 hover:border-[#4FE0D5]/40 bg-black/20" data-testid={`sms-row-${row.id}`}>
            <div className="flex items-center gap-2 min-w-0">
                <MessageSquare size={12} className="text-white/40 flex-shrink-0" />
                <div className="min-w-0">
                    <div className="text-xs text-white/80 truncate">
                        <span className="font-mono">{row.to_e164 || "?"}</span> · <span className="text-white/50">{KIND_LABEL[row.kind] || row.kind}</span>
                    </div>
                    {(row.error_code || row.error_message) && (
                        <div className="text-[10px] text-rose-400/80 truncate" title={row.error_message || ""}>
                            {row.error_code ? `Code ${row.error_code}` : ""}{row.error_code && row.error_message ? " — " : ""}{row.error_message || ""}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <StatusBadge row={row} />
                <span className="text-[10px] text-white/40 font-mono">{new Date(row.updated_at || row.created_at).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}</span>
            </div>
        </div>
    );
}

function ExpandedSmsLog({ registrationId, refreshKey }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/registrations/${registrationId}/sms_log`);
            setRows(data);
        } finally {
            setLoading(false);
        }
    }, [registrationId]);
    useEffect(() => { refresh(); }, [refresh, refreshKey]);
    // Live refresh while statuses are still in-flight
    useEffect(() => {
        const inflight = rows.some(r => ["queued", "accepted", "sending", "sent", "scheduled"].includes(r.status));
        if (!inflight) return undefined;
        const id = setInterval(refresh, 4000);
        return () => clearInterval(id);
    }, [rows, refresh]);
    return (
        <div className="py-2 px-3 bg-black/30 border-t border-white/5" data-testid={`sms-log-${registrationId}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="label-mini">SMS Twilio (statut en direct)</div>
                <button onClick={refresh} className="text-white/50 hover:text-[#4FE0D5] flex items-center gap-1 text-[10px]" data-testid={`btn-refresh-sms-${registrationId}`}>
                    <RefreshCcw size={11} /> Rafraîchir
                </button>
            </div>
            {loading && rows.length === 0 && <div className="text-xs text-white/40">Chargement...</div>}
            {!loading && rows.length === 0 && <div className="text-xs text-white/40">Aucun SMS enregistré pour cette inscription.</div>}
            <div className="grid gap-1">
                {rows.map(r => <SmsRow key={r.id} row={r} />)}
            </div>
        </div>
    );
}

export default function AdminRegistrations() {
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState("all");
    const [catFilter, setCatFilter] = useState("all");
    const [expanded, setExpanded] = useState({});
    const [refreshKey, setRefreshKey] = useState(0);
    const [showManual, setShowManual] = useState(false);

    const load = () => api.get("/registrations").then(r => setList(r.data));
    useEffect(() => { load(); }, []);
    // Auto-refresh while any manager SMS is still in-flight (queued/sent/sending) so
    // the admin sees "Livré" / "Non livré" without having to refresh manually.
    useEffect(() => {
        const inflight = list.some(r => {
            const a = r.manager_received_sms?.status;
            const b = r.manager_link_sms?.status;
            const pending = (s) => s && ["queued", "accepted", "sending", "sent", "scheduled"].includes(s);
            return pending(a) || pending(b);
        });
        if (!inflight) return undefined;
        const id = setInterval(load, 4000);
        return () => clearInterval(id);
    }, [list]);

    const filtered = list.filter(r => {
        if (filter !== "all" && r.payment_status !== filter) return false;
        if (catFilter !== "all" && r.category !== catFilter) return false;
        return true;
    });
    const totalPaid = list.filter(r => r.payment_status === "paid").reduce((s, r) => s + (r.amount || 0), 0);

    const toggleExpand = (rid) => setExpanded(e => ({ ...e, [rid]: !e[rid] }));
    const bumpRefresh = (rid) => { setExpanded(e => ({ ...e, [rid]: true })); setRefreshKey(k => k + 1); };

    const markPaid = async (rid) => {
        // Wrapper kept for clarity, routes to the unified setStatus.
        await setStatus(rid, "paid");
    };
    const resendReceivedSms = async (rid) => {
        if (!window.confirm("Renvoyer le SMS « Inscription reçue » au responsable de cette équipe ?")) return;
        try {
            const { data } = await api.post(`/registrations/${rid}/resend_received_sms`);
            bumpRefresh(rid);
            const sms = data.sms || {};
            const summary = data.sms_sent
                ? `✅ SMS de réception accepté par Twilio (statut initial : ${sms.status || "queued"}).\n\nSuivez la livraison dans la colonne « SMS Réception ».`
                : `⚠️ SMS NON envoyé\nRaison : ${sms.reason || "?"}\nErreur Twilio : ${sms.error || "(aucune)"}\nNuméro normalisé : ${sms.e164 || sms.raw_to || "(invalide)"}`;
            alert(summary);
        } catch (e) {
            alert("Erreur : " + (e?.response?.data?.detail || e.message));
        }
    };
    const resendLink = async (rid) => {
        if (!window.confirm("Régénérer un nouveau lien d'accès équipe et le renvoyer par SMS ? L'ancien lien sera invalidé.")) return;
        try {
            const { data } = await api.post(`/registrations/${rid}/resend_magic_link`);
            await navigator.clipboard.writeText(data.magic_link).catch(() => {});
            bumpRefresh(rid);
            const sms = data.sms || {};
            const summary = data.sms_sent
                ? `✅ SMS accepté par Twilio (statut initial: ${sms.status || "queued"}).\n\nSurveillez la livraison dans la ligne dépliée sous l'inscription.`
                : `⚠️ SMS NON envoyé\nRaison: ${sms.reason || "?"}\nErreur Twilio: ${sms.error || "(aucune)"}\nNuméro normalisé: ${sms.e164 || sms.raw_to || "(invalide)"}`;
            alert(`Nouveau lien copié dans le presse-papier.\n\n${summary}\n\nLien :\n${data.magic_link}`);
        } catch (e) {
            alert("Erreur : " + (e?.response?.data?.detail || e.message));
        }
    };
    const setStatus = async (rid, status) => {
        const confirmMsg = status === "cancelled"
            ? "Annuler cette inscription ? L'équipe sera marquée « Annulée » et le SMS de confirmation ne sera plus envoyé."
            : status === "paid"
                ? "Marquer cette inscription comme « Confirmée » (payée) ? Cela crée le compte d'équipe et envoie le SMS du lien d'accès au responsable."
                : `Changer le statut vers « ${status} » ?`;
        if (!window.confirm(confirmMsg)) return;
        try {
            await api.patch(`/registrations/${rid}/status`, { status });
            await load();
            if (status === "paid") bumpRefresh(rid);
        } catch (e) {
            alert("Erreur : " + (e?.response?.data?.detail || e.message));
        }
    };

    const del = async (rid) => {
        if (!window.confirm("Supprimer cette inscription ?")) return;
        await api.delete(`/registrations/${rid}`);
        load();
    };

    const exportCsv = () => {
        const cols = ["team_name", "category", "city", "manager_name", "email", "phone", "payment_status", "amount", "currency", "created_at", "paid_at"];
        const rows = [cols.join(",")];
        filtered.forEach(r => rows.push(cols.map(c => `"${(r[c] ?? "").toString().replace(/"/g, '""')}"`).join(",")));
        const blob = new Blob([rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `inscriptions-${new Date().toISOString().slice(0,10)}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-registrations">
            <h1 className="font-display text-4xl uppercase tracking-widest mb-2">Inscriptions</h1>
            <p className="text-white/60 mb-6 text-sm">Équipes inscrites & paiements Interac. Cliquez sur la flèche pour voir le statut de livraison des SMS en temps réel.</p>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="glass-card p-5 border-l-4 border-[#7FD93C]"><div className="label-mini">Payées</div><div className="font-display text-4xl text-[#7FD93C]">{list.filter(r => r.payment_status === "paid").length}</div></div>
                <div className="glass-card p-5 border-l-4 border-[#4FE0D5]"><div className="label-mini">En attente Interac</div><div className="font-display text-4xl text-[#4FE0D5]">{list.filter(r => r.payment_status === "pending_etransfer" || r.payment_status === "pending").length}</div></div>
                <div className="glass-card p-5 border-l-4 border-white/30"><div className="label-mini">Total perçu</div><div className="font-display text-4xl">{totalPaid} $ <span className="text-base text-white/50">CAD</span></div></div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {[
                    { v: "all",                label: "Tout" },
                    { v: "paid",               label: "Confirmées" },
                    { v: "pending_etransfer",  label: "Attente Interac" },
                    { v: "cancelled",          label: "Annulées" },
                    { v: "expired",            label: "Expirées" },
                    { v: "failed",             label: "Échec" },
                ].map(s => {
                    const count = s.v === "all" ? list.length : list.filter(r => r.payment_status === s.v).length;
                    return (
                        <button key={s.v} onClick={() => setFilter(s.v)} className={`px-4 py-1.5 font-display text-sm tracking-widest uppercase flex items-center gap-2 ${filter === s.v ? "bg-[#7FD93C] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`filter-${s.v}`}>
                            {s.label} <span className="text-[10px] opacity-70">{count}</span>
                        </button>
                    );
                })}
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="label-mini">Catégorie:</span>
                {["all", ...TOURNAMENT.categories].map(c => (
                    <button key={c} onClick={() => setCatFilter(c)} className={`px-3 py-1 text-xs font-display tracking-wider uppercase ${catFilter === c ? "bg-[#4FE0D5] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`} data-testid={`cat-filter-${c}`}>
                        {c === "all" ? "Toutes" : c}
                    </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => setShowManual(true)} className="btn-ghost text-[#7FD93C] border-[#7FD93C]/40 hover:border-[#7FD93C] flex items-center gap-2" data-testid="btn-manual-registration"><UserPlus size={14} /> Inscription manuelle</button>
                    <button onClick={exportCsv} className="btn-ghost flex items-center gap-2" data-testid="export-csv"><Download size={14} /> Export CSV</button>
                </div>
            </div>
            <div className="hidden lg:block overflow-x-auto"><table className="tx-table">
                <thead><tr><th></th><th>Équipe</th><th>Catégorie</th><th>Ville</th><th>Responsable</th><th>Tél.</th><th>Montant</th><th>Statut</th><th title="SMS « inscription reçue »">SMS Récep.</th><th title="SMS avec lien d'accès (après paiement)">SMS Lien</th><th>Date</th><th></th></tr></thead>
                <tbody>{filtered.map(r => (
                    <React.Fragment key={r.id}>
                        <tr data-testid={`reg-row-${r.id}`}>
                            <td><button onClick={() => toggleExpand(r.id)} className="text-white/50 hover:text-[#4FE0D5]" data-testid={`btn-expand-${r.id}`} aria-label="Voir SMS">
                                {expanded[r.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button></td>
                            <td className="font-semibold">{r.team_name}</td>
                            <td>{r.category}</td>
                            <td>{r.city}</td>
                            <td>{r.manager_name}<div className="text-[10px] text-white/40">{r.email}</div></td>
                            <td className="text-xs">{r.phone}</td>
                            <td className="font-display text-lg">{r.amount} $</td>
                            <td><PaymentBadge reg={r} /></td>
                            <td><ManagerSmsCell sms={r.manager_received_sms} label="Réception" /></td>
                            <td><ManagerSmsCell sms={r.manager_link_sms} label="Lien" /></td>
                            <td className="text-xs text-white/60">{new Date(r.created_at).toLocaleDateString("fr-CA")}</td>
                            <td><div className="flex gap-1 justify-end flex-wrap">
                                {r.payment_status !== "paid" && r.payment_status !== "cancelled" && <button onClick={() => setStatus(r.id, "paid")} className="btn-ghost text-[#7FD93C] border-[#7FD93C]/30 hover:border-[#7FD93C]" title="Confirmer (marquer payé)" data-testid={`btn-mark-paid-${r.id}`}><CheckCircle2 size={12} /></button>}
                                {r.payment_status !== "cancelled" && <button onClick={() => setStatus(r.id, "cancelled")} className="btn-ghost text-rose-400 border-rose-400/30 hover:border-rose-400" title="Annuler l'inscription" data-testid={`btn-cancel-${r.id}`}><Ban size={12} /></button>}
                                {r.payment_status === "cancelled" && <button onClick={() => setStatus(r.id, "pending_etransfer")} className="btn-ghost text-[#4FE0D5] border-[#4FE0D5]/30 hover:border-[#4FE0D5]" title="Réactiver (remettre en attente Interac)" data-testid={`btn-reactivate-${r.id}`}><RefreshCcw size={12} /></button>}
                                <button onClick={() => resendReceivedSms(r.id)} className="btn-ghost text-[#4FE0D5] border-[#4FE0D5]/30 hover:border-[#4FE0D5]" title="Renvoyer SMS « Inscription reçue »" data-testid={`btn-resend-received-${r.id}`}><Bell size={12} /></button>
                                {r.payment_status === "paid" && r.team_id && <Link to="/admin/teams" className="btn-ghost text-[#4FE0D5] border-[#4FE0D5]/30 hover:border-[#4FE0D5]" title="Gérer l'équipe créée" data-testid={`btn-manage-team-${r.id}`}><Users size={12} /></Link>}
                                {r.payment_status === "paid" && <button onClick={() => resendLink(r.id)} className="btn-ghost text-[#7FD93C] border-[#7FD93C]/30 hover:border-[#7FD93C]" title="Renvoyer SMS lien d'accès" data-testid={`btn-resend-link-${r.id}`}><Send size={12} /></button>}
                                <button onClick={() => del(r.id)} className="btn-ghost text-rose-400 border-rose-400/30" title="Supprimer définitivement"><Trash2 size={12} /></button>
                            </div></td>
                        </tr>
                        {expanded[r.id] && (
                            <tr data-testid={`sms-expand-${r.id}`}>
                                <td colSpan="12" className="!p-0">
                                    <ExpandedSmsLog registrationId={r.id} refreshKey={refreshKey} />
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                ))}{filtered.length === 0 && <tr><td colSpan="12" className="text-center text-white/50 py-8">Aucune inscription pour ce filtre.</td></tr>}</tbody>
            </table></div>

            {/* Mobile card view */}
            <div className="lg:hidden grid gap-3">
                {filtered.map(r => (
                    <div key={r.id} className="glass-card p-4" data-testid={`reg-card-${r.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold truncate">{r.team_name}</div>
                                <div className="text-[10px] tracking-widest font-bold text-[#4FE0D5]">{r.category} · {r.city}</div>
                            </div>
                            <PaymentBadge reg={r} />
                        </div>
                        <div className="text-xs text-white/70 mb-3 space-y-1">
                            <div><span className="text-white/40">Resp. :</span> {r.manager_name}</div>
                            <div><span className="text-white/40">Tél. :</span> <a href={`tel:${r.phone}`} className="text-[#4FE0D5]">{r.phone}</a></div>
                            <div><span className="text-white/40">Courriel :</span> <a href={`mailto:${r.email}`} className="text-[#4FE0D5] break-all">{r.email}</a></div>
                            <div><span className="text-white/40">Montant :</span> <span className="font-display text-base">{r.amount} $</span></div>
                            <div><span className="text-white/40">Date :</span> {new Date(r.created_at).toLocaleDateString("fr-CA")}</div>
                        </div>
                        <div className="flex items-center gap-3 mb-3 text-xs">
                            <div><span className="text-white/40 mr-1">Récep:</span><ManagerSmsCell sms={r.manager_received_sms} label="Réception" /></div>
                            <div><span className="text-white/40 mr-1">Lien:</span><ManagerSmsCell sms={r.manager_link_sms} label="Lien" /></div>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-white/10">
                            {r.payment_status !== "paid" && r.payment_status !== "cancelled" && <button onClick={() => setStatus(r.id, "paid")} className="btn-ghost text-[#7FD93C] border-[#7FD93C]/30 text-xs flex items-center gap-1"><CheckCircle2 size={12} /> Confirmer</button>}
                            {r.payment_status !== "cancelled" && <button onClick={() => setStatus(r.id, "cancelled")} className="btn-ghost text-rose-400 border-rose-400/30 text-xs flex items-center gap-1"><Ban size={12} /> Annuler</button>}
                            {r.payment_status === "cancelled" && <button onClick={() => setStatus(r.id, "pending_etransfer")} className="btn-ghost text-[#4FE0D5] border-[#4FE0D5]/30 text-xs flex items-center gap-1"><RefreshCcw size={12} /> Réactiver</button>}
                            <button onClick={() => resendReceivedSms(r.id)} className="btn-ghost text-[#4FE0D5] border-[#4FE0D5]/30 text-xs flex items-center gap-1"><Bell size={12} /> Renvoyer récep.</button>
                            {r.payment_status === "paid" && <button onClick={() => resendLink(r.id)} className="btn-ghost text-[#7FD93C] border-[#7FD93C]/30 text-xs flex items-center gap-1"><Send size={12} /> Renvoyer lien</button>}
                            {r.payment_status === "paid" && r.team_id && <Link to="/admin/teams" className="btn-ghost text-[#4FE0D5] border-[#4FE0D5]/30 text-xs flex items-center gap-1"><Users size={12} /> Équipe</Link>}
                            <button onClick={() => del(r.id)} className="btn-ghost text-rose-400 border-rose-400/30 text-xs"><Trash2 size={12} /></button>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className="text-center text-white/50 py-8">Aucune inscription pour ce filtre.</div>}
            </div>

            <ManualRegistrationModal
                open={showManual}
                onClose={() => setShowManual(false)}
                onCreated={() => { load(); }}
            />
        </div>
    );
}
